import { Database, FileText, Webhook, Workflow } from 'lucide-react';
import { type ComponentType, useState } from 'react';
import type { FeatureConfig } from '~/lib/builder-types';
import { resolveTechSpec } from '~/lib/tech-spec/index';
import type {
  FeatureTechSpecDelta,
  TechStackConfig,
} from '~/lib/tech-spec/types';
import TechSpecSection from './TechSpecSection';

type Tab = 'pages' | 'flows' | 'endpoints' | 'db';

interface Props {
  featureSlug: string;
  featureName: string;
  stack: TechStackConfig;
  featureConfig?: FeatureConfig;
  customizations?: FeatureTechSpecDelta;
  onCustomize: (delta: Partial<FeatureTechSpecDelta>) => void;
}

const TABS: {
  id: Tab;
  label: string;
  icon: ComponentType<{ className?: string }>;
}[] = [
  { id: 'pages', label: 'Pages', icon: FileText },
  { id: 'flows', label: 'UX Flows', icon: Workflow },
  { id: 'endpoints', label: 'Endpoints', icon: Webhook },
  { id: 'db', label: 'DB Tables', icon: Database },
];

export default function FeatureTechCard({
  featureSlug,
  featureName,
  stack,
  featureConfig,
  customizations,
  onCustomize,
}: Props) {
  const [tab, setTab] = useState<Tab>('pages');

  const spec = resolveTechSpec(
    featureSlug,
    stack,
    featureConfig,
    customizations,
  );

  const counts = {
    pages: spec.pages.length,
    flows: spec.uxFlows.length,
    endpoints: spec.endpoints.length,
    db: spec.dbTables.length,
  };

  function handleDelta(delta: Partial<FeatureTechSpecDelta>) {
    // Merge delta with existing customizations so we accumulate removals
    onCustomize(delta);
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-900">{featureName}</h3>
        <span className="text-xs text-gray-400">
          {counts.pages}p · {counts.endpoints}ep · {counts.db}t
        </span>
      </div>

      {/* Tab bar */}
      <div className="flex border-b border-gray-100">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            className={`flex items-center gap-1.5 px-4 py-2 text-xs font-medium transition-colors ${
              tab === t.id
                ? 'text-indigo-600 border-b-2 border-indigo-600 -mb-px'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <t.icon className="h-3.5 w-3.5" />
            {t.label}
            {counts[t.id] > 0 && (
              <span className="ml-1.5 text-[10px] text-gray-400">
                {counts[t.id]}
              </span>
            )}
          </button>
        ))}
      </div>

      <div className="p-4">
        {tab === 'pages' && (
          <TechSpecSection
            type="pages"
            pages={spec.pages}
            onCustomize={handleDelta}
          />
        )}
        {tab === 'flows' && (
          <TechSpecSection
            type="uxFlows"
            uxFlows={spec.uxFlows}
            onCustomize={handleDelta}
          />
        )}
        {tab === 'endpoints' && (
          <TechSpecSection
            type="endpoints"
            endpoints={spec.endpoints}
            onCustomize={handleDelta}
          />
        )}
        {tab === 'db' && (
          <TechSpecSection
            type="dbTables"
            dbTables={spec.dbTables}
            onCustomize={handleDelta}
          />
        )}
      </div>
    </div>
  );
}
